import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import EventCard from '../components/EventCard'
import { eventsAPI } from '../services/api'
import { Search, Filter, Calendar, MapPin } from 'lucide-react'

const Events = () => {
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')
  const [showUpcoming, setShowUpcoming] = useState(true)

  const categories = [
    { value: '', label: 'All Categories' },
    { value: 'workshop', label: 'Workshops' },
    { value: 'seminar', label: 'Seminars' },
    { value: 'field-day', label: 'Field Days' },
    { value: 'conference', label: 'Conferences' },
    { value: 'training', label: 'Training' },
  ]

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true)
        setError(null) // Clear any previous errors
        const params = {}
        
        if (searchTerm) params.search = searchTerm
        if (selectedCategory) params.category = selectedCategory
        if (showUpcoming) params.upcoming = 'true'

        const response = await eventsAPI.getAll(params)
        
        // Ensure response has expected structure
        if (response && response.data && response.data.data) {
          setEvents(response.data.data)
        } else {
          throw new Error('Invalid response format')
        }
      } catch (err) {
        console.error('Error fetching events:', err)
        setError('Failed to load events. Please refresh the page or try again later.')
        setEvents([]) // Clear events on error
      } finally {
        setLoading(false)
      }
    }

    fetchEvents()
  }, [searchTerm, selectedCategory, showUpcoming])

  const handleSearch = (e) => {
    e.preventDefault()
    setSearchTerm(e.target.value)
  }

  const handleCategoryChange = (category) => {
    setSelectedCategory(category)
  }

  const upcomingEvents = events.filter(event => new Date(event.date) > new Date())
  const pastEvents = events.filter(event => new Date(event.date) <= new Date())

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <section className="bg-white py-16">
        <div className="container-custom text-center">
          <h1 className="section-heading mb-4">Events & Workshops</h1>
          <p className="subheading max-w-3xl">
            Join our educational events, workshops, and field days to learn about 
            the latest in sustainable agriculture and farming techniques.
          </p>
        </div>
      </section>

      {/* Filters and Search */}
      <section className="py-8 bg-white border-b">
        <div className="container-custom">
          <div className="flex flex-col lg:flex-row gap-6 items-center justify-between">
            {/* Search */}
            <div className="flex-1 max-w-md">
              <form onSubmit={handleSearch} className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search events..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-earth-green focus:border-transparent"
                />
              </form>
            </div>

            {/* Category Filter */}
            <div className="flex items-center space-x-4">
              <Filter className="h-5 w-5 text-gray-600" />
              <select
                value={selectedCategory}
                onChange={(e) => handleCategoryChange(e.target.value)}
                className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-earth-green focus:border-transparent"
              >
                {categories.map((category) => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Upcoming/Past Toggle */}
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowUpcoming(true)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  showUpcoming 
                    ? 'bg-earth-green text-white' 
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
              >
                Upcoming
              </button>
              <button
                onClick={() => setShowUpcoming(false)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  !showUpcoming 
                    ? 'bg-earth-green text-white' 
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
              >
                All Events
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Events Grid */}
      <section className="section-padding">
        <div className="container-custom">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-earth-green"></div>
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <p className="text-red-600 text-lg">{error}</p>
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-20">
              <div className="bg-white p-8 rounded-xl shadow-lg max-w-md mx-auto">
                <Calendar className="h-16 w-16 text-brand-blue mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {showUpcoming ? 'No Upcoming Events' : 'No Past Events'}
                </h3>
                <p className="text-gray-600">
                  {showUpcoming 
                    ? 'Check back soon for new events and workshops.' 
                    : 'No past events found matching your criteria.'
                  }
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="mb-8">
                <p className="text-gray-600">
                  Showing {events.length} event{events.length !== 1 ? 's' : ''}
                  {searchTerm && ` for "${searchTerm}"`}
                  {selectedCategory && ` in ${categories.find(c => c.value === selectedCategory)?.label}`}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {events.map((event) => (
                  <EventCard key={event.id} event={event} />
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Event Types Info */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Types of Events We Host
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              From hands-on workshops to educational seminars, we offer a variety of events 
              to help you improve your farming practices.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-xl border border-gray-200">
              <div className="bg-earth-green p-3 rounded-lg w-fit mb-4">
                <Calendar className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Workshops</h3>
              <p className="text-gray-600">
                Hands-on learning sessions where you can practice new techniques and get 
                direct guidance from our agricultural experts.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl border border-gray-200">
              <div className="bg-olive p-3 rounded-lg w-fit mb-4">
                <MapPin className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Field Days</h3>
              <p className="text-gray-600">
                Visit our research farms to see products in action and learn about 
                real-world applications and results.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl border border-gray-200">
              <div className="bg-brand-blue p-3 rounded-lg w-fit mb-4">
                <Calendar className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Seminars</h3>
              <p className="text-gray-600">
                Educational presentations covering the latest research, trends, and 
                best practices in sustainable agriculture.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Events
